import { useCallback, useEffect, useState } from "react";
import { api, errDetail, fmtEur, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { ShoppingCart, Send, PackageCheck, ReceiptText } from "lucide-react";

const STATUS = {
  created: ["bg-gray-100 text-gray-700", "Angelegt"],
  sent: ["bg-blue-100 text-blue-700", "Versandt"],
  error: ["bg-red-100 text-red-700", "ERP-Fehler"],
  partially_received: ["bg-amber-100 text-amber-800", "Teilweise erhalten"],
  received: ["bg-green-100 text-green-700", "Erhalten"],
  closed: ["bg-gray-100 text-gray-500", "Abgeschlossen"],
};
const St = ({ s }) => { const [c, l] = STATUS[s] || ["bg-gray-100", s]; return <Badge className={c}>{l}</Badge>; };

export default function PurchaseOrders() {
  const [rows, setRows] = useState([]);
  const [caps, setCaps] = useState([]);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => { api.get("/srm/purchase-orders").then((r) => setRows(r.data || [])).catch((e) => toast.error(errDetail(e))); }, []);
  useEffect(() => { api.get("/srm/my-caps").then((r) => setCaps(r.data.caps || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="p-6 max-w-[1300px] mx-auto" data-testid="po-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center"><ShoppingCart className="h-5 w-5 text-[#F5A623]" /></div>
        <div><h1 className="text-2xl font-bold text-[#1B2A4A]">Bestellungen</h1><p className="text-sm text-gray-500">PO · Wareneingang · Rechnungsprüfung (3-Way-Match)</p></div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Nummer</TableHead><TableHead>Lieferant</TableHead><TableHead>Werk</TableHead><TableHead>Wert</TableHead><TableHead>Status</TableHead><TableHead>Angelegt</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">Keine Bestellungen</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer hover:bg-gray-50" data-testid={`po-row-${r.po_number}`} onClick={() => setDetail(r.id)}>
                <TableCell className="font-mono font-medium text-[#1B2A4A]">{r.po_number}</TableCell>
                <TableCell>{r.supplier_name}{r.non_approved_source && <Badge className="ml-2 bg-amber-100 text-amber-800">nicht freigegebener Bezug</Badge>}</TableCell>
                <TableCell>{r.plant_name || "–"}</TableCell><TableCell>{fmtEur(r.total_value, r.currency)}</TableCell>
                <TableCell><St s={r.status} /></TableCell><TableCell className="text-sm text-gray-500">{fmtDate(r.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {detail && <PoDetailDialog id={detail} caps={caps} onClose={(ch) => { setDetail(null); if (ch) load(); }} />}
    </div>
  );
}

function PoDetailDialog({ id, caps, onClose }) {
  const [po, setPo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [grQty, setGrQty] = useState({});
  const [inv, setInv] = useState({ invoice_number: "", lines: {} });
  const load = useCallback(() => { api.get(`/srm/purchase-orders/${id}`).then((r) => setPo(r.data)).catch((e) => toast.error(errDetail(e))); }, [id]);
  useEffect(() => { load(); }, [load]);
  const canApprove = caps.includes("po_approve") || caps.includes("srm_admin");
  const canReceive = caps.includes("po_receive") || caps.includes("srm_admin");
  const act = async (fn, msg, close) => { setBusy(true); try { await fn(); toast.success(msg); if (close) onClose(true); else load(); } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); } };
  const send = () => act(() => api.post(`/srm/purchase-orders/${id}/send`, {}), "An ERP versandt");
  const bookGr = () => act(() => api.post(`/srm/purchase-orders/${id}/goods-receipts`, {
    line_items: po.line_items.map((l) => ({ po_line_id: l.id, qty_received: Number(grQty[l.id] || 0) })).filter((x) => x.qty_received > 0),
  }), "Wareneingang gebucht", true);
  const bookInv = () => act(() => api.post(`/srm/purchase-orders/${id}/invoices`, {
    invoice_number: inv.invoice_number,
    line_items: po.line_items.map((l) => ({ po_line_id: l.id, qty: Number(inv.lines[l.id]?.qty || 0), unit_price: Number(inv.lines[l.id]?.unit_price || 0) })).filter((x) => x.qty > 0),
  }), "Rechnung erfasst", true);
  if (!po) return null;
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-3xl" data-testid="po-detail-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-mono">{po.po_number} <St s={po.status} /></DialogTitle>
          <DialogDescription>{po.supplier_name} · {po.plant_name} · aus {po.pr_number} · Freigeber {po.approved_by_name || "–"}</DialogDescription></DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          {po.non_approved_source && <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="po-nonapproved-banner">Nicht freigegebener Bezugsweg — Begründung: {po.non_approved_justification || "–"}</div>}
          <Table><TableHeader><TableRow><TableHead>Artikel</TableHead><TableHead>Menge</TableHead><TableHead>Preis</TableHead><TableHead>Zeile</TableHead></TableRow></TableHeader>
            <TableBody>{po.line_items.map((l) => (<TableRow key={l.id}><TableCell>{l.article_name || l.description || "–"}</TableCell><TableCell>{l.qty} {l.unit}</TableCell><TableCell>{fmtEur(l.unit_price)}</TableCell><TableCell>{fmtEur(l.line_total)}</TableCell></TableRow>))}</TableBody></Table>
          <div className="text-right font-semibold text-[#1B2A4A]">Summe: {fmtEur(po.total_value, po.currency)}</div>

          {po.status === "created" && canApprove && <Button data-testid="po-send-btn" disabled={busy} onClick={send}><Send className="h-4 w-4 mr-1" /> An ERP senden</Button>}

          {(po.goods_receipts || []).length > 0 && (
            <div><h4 className="text-sm font-semibold text-[#1B2A4A] mb-1">Wareneingänge</h4>
              {po.goods_receipts.map((g) => <div key={g.id} className="text-sm text-gray-600" data-testid={`gr-${g.gr_number}`}>{g.gr_number} · {g.received_by_name} · {fmtDate(g.created_at)}</div>)}</div>
          )}
          {["sent", "partially_received", "created"].includes(po.status) && canReceive && (
            <div className="border-t pt-3 space-y-2">
              <h4 className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-1"><PackageCheck className="h-4 w-4" /> Wareneingang buchen</h4>
              {po.line_items.map((l) => (
                <div key={l.id} className="flex items-center gap-2"><span className="text-sm flex-1">{l.article_name || l.id}</span>
                  <Input className="w-28" type="number" placeholder="Menge" data-testid={`gr-qty-${l.id}`} value={grQty[l.id] || ""} onChange={(e) => setGrQty((s) => ({ ...s, [l.id]: e.target.value }))} /></div>
              ))}
              <Button size="sm" data-testid="gr-book-btn" disabled={busy} onClick={bookGr}>Wareneingang buchen</Button>
            </div>
          )}

          {(po.invoices || []).length > 0 && (
            <div><h4 className="text-sm font-semibold text-[#1B2A4A] mb-1">Rechnungen</h4>
              {po.invoices.map((v) => <div key={v.id} className="text-sm" data-testid={`inv-${v.invoice_number}`}>{v.invoice_number} · {fmtEur(v.amount, v.currency)} · <Badge className={v.status === "matched" || v.status === "posted" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>{v.status}</Badge></div>)}</div>
          )}
          {["received", "partially_received", "sent"].includes(po.status) && canApprove && (
            <div className="border-t pt-3 space-y-2">
              <h4 className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-1"><ReceiptText className="h-4 w-4" /> Rechnung erfassen (3-Way-Match)</h4>
              <Input placeholder="Rechnungsnummer" data-testid="inv-number" value={inv.invoice_number} onChange={(e) => setInv((s) => ({ ...s, invoice_number: e.target.value }))} />
              {po.line_items.map((l) => (
                <div key={l.id} className="flex items-center gap-2"><span className="text-sm flex-1">{l.article_name || l.id}</span>
                  <Input className="w-24" type="number" placeholder="Menge" data-testid={`inv-qty-${l.id}`} onChange={(e) => setInv((s) => ({ ...s, lines: { ...s.lines, [l.id]: { ...s.lines[l.id], qty: e.target.value } } }))} />
                  <Input className="w-24" type="number" placeholder="Preis" data-testid={`inv-price-${l.id}`} onChange={(e) => setInv((s) => ({ ...s, lines: { ...s.lines, [l.id]: { ...s.lines[l.id], unit_price: e.target.value } } }))} /></div>
              ))}
              <Button size="sm" data-testid="inv-book-btn" disabled={busy || !inv.invoice_number} onClick={bookInv}>Rechnung prüfen</Button>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onClose(false)}>Schließen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
